/**
 * FormsAPI — client for the Forms Admin backend (PHP/FormHandler.php).
 *
 * Migrated 2026-06-08 from raw axios to apiCall() so every request
 * carries the verified Firebase ID token. FormHandler.php now requires
 * require_admin_access() at the top — without the token, every call
 * 401s.
 *
 * Response shape note: this module deliberately preserves the legacy
 * `{ data: <body> }` envelope its callers already expect (FormsAdmin.js
 * reads response.data.forms, response.data.fields, etc.). We wrap the
 * parsed JSON from apiCall() in `{ data }` so the migration is a single
 * file change rather than a bunch of churn at every call site.
 *
 * Error handling: apiCall throws ApiError on non-2xx with .status and
 * .body. The error path in FormsAdmin.js still does
 * `err?.response?.data?.error` which is axios-shaped — that won't
 * resolve on an ApiError. We patch the throw site to additionally
 * surface `.body?.error` so the protected-field 403 message keeps
 * reaching the user. (See FormsAdmin.handleSaveForm.)
 */

import { apiCall } from './api';

// Wrap apiCall responses in the legacy `{ data }` envelope so callers
// reading `response.data.forms` keep working without per-call churn.
async function wrappedGet(endpoint, query) {
    const data = await apiCall(endpoint, query ? { query } : undefined);
    return { data };
}

async function wrappedPost(endpoint, body) {
    const data = await apiCall(endpoint, { method: 'POST', body });
    return { data };
}

const FormsAPI = {
    getForms: () =>
        wrappedGet('FormHandler.php', { action: 'getForms' }),
    getFormsProject: (projectsId) =>
        wrappedGet('FormHandler.php', { action: 'getFormsProject', projectsId }),
    getFields: (projectsId) =>
        wrappedGet('FormHandler.php', { action: 'getFields', projectsId }),
    saveForm: (form) =>
        wrappedPost('FormHandler.php?action=saveForm', form),
    createField: (field) =>
        wrappedPost('FormHandler.php?action=createField', field),
    getProjects: () =>
        wrappedGet('FormHandler.php', { action: 'getProjects' }),
    updateField: (field) =>
        wrappedPost('FormHandler.php?action=updateField', field),
    deleteField: (fieldId) =>
        wrappedPost('FormHandler.php?action=deleteField', { fieldId }),
};

export default FormsAPI;
